export interface ITokenClaims {
  [claim: string]: unknown
}

export interface ITokenUserInfo {
  name: string
  email: string
  initials: string
}

export interface ITokenAccessInfo {
  claims: ITokenClaims
  customerId: string
  organization: unknown
  organizationName: string
  hasTenantClaim: boolean
  userInfo: ITokenUserInfo
}

export interface IOidcUserLike {
  access_token?: string
  profile?: ITokenClaims
}

const ORGANIZATION_CLAIM_KEYS = ['organization']
const CUSTOMER_CLAIM_KEYS = ['cloud:customerId']

/**
 * Decodes the payload segment of a JWT without validating its signature.
 * Returns null when the token is missing, opaque or malformed.
 */
export function decodeJwtPayload(token?: string | null): ITokenClaims | null {
  if (typeof token !== 'string' || token.trim() === '') {
    return null
  }
  const payloadSegment = token.split('.')[1]
  if (!payloadSegment) {
    return null
  }
  try {
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/')
    const padding = (4 - (base64.length % 4)) % 4
    const binary = atob(base64 + '='.repeat(padding))
    const json = decodeURIComponent(
      binary
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    )
    const claims = JSON.parse(json)
    return claims && typeof claims === 'object' ? claims : null
  } catch (error) {
    console.error('Failed to decode token payload:', error)
    return null
  }
}

function isClaimPresent(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim() !== ''
  }
  if (typeof value === 'number') {
    return !Number.isNaN(value) && value !== 0
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (Array.isArray(value)) {
    return value.some((entry) => isClaimPresent(entry))
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0
  }
  return false
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * The customer id may be written as a string, a number or - as Keycloak
 * renders multi valued attributes - an array. Reduce it to a single value.
 */
function asCustomerId(value: unknown): string {
  if (Array.isArray(value)) {
    const entry = value.find((item) => asCustomerId(item) !== '')
    return entry === undefined ? '' : asCustomerId(entry)
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? '' : String(value)
  }
  return asString(value)
}

function findClaim(claims: ITokenClaims, keys: string[]): unknown {
  const key = keys.find((claimKey) => isClaimPresent(claims[claimKey]))
  return key ? claims[key] : null
}

/**
 * Reads the customer id out of the organization claim, which is shaped as:
 *
 *   "organization": { "3xLOGIC": { "cloud:customerId": ["1"] } }
 *
 * Only the first organization is considered - a VIGIL token is expected to
 * carry exactly one, and that one is the tenant the session belongs to.
 */
export function getOrganizationCustomerId(
  organization: unknown
): {
  organizationName: string
  customerId: string
} {
  if (!isPlainObject(organization)) {
    return { organizationName: '', customerId: '' }
  }
  const organizationName = Object.keys(organization)[0]
  if (!organizationName) {
    return { organizationName: '', customerId: '' }
  }
  const attributes = organization[organizationName]
  const customerId = isPlainObject(attributes)
    ? asCustomerId(findClaim(attributes, CUSTOMER_CLAIM_KEYS))
    : ''

  return { organizationName, customerId }
}

/** Builds the display info shown on screens that only have the token to work with. */
export function getTokenUserInfo(claims: ITokenClaims): ITokenUserInfo {
  const email = asString(claims.email)
  const fullName = asString(claims.name)
  const givenAndFamily = [
    asString(claims.given_name),
    asString(claims.family_name),
  ]
    .filter((part) => part !== '')
    .join(' ')
  const name =
    fullName || givenAndFamily || asString(claims.preferred_username) || email
  const initials = name
    .split(/\s+/)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('')

  return { name, email, initials }
}

/**
 * Reads the tenant (customer / organization) claims out of an OIDC user so the
 * UI can tell - before calling the API - whether the token is usable here.
 * Access is granted when the first organization on the token carries a non
 * empty 'cloud:customerId'; a flat customer id claim is accepted as a fallback.
 * Access token claims win over id token (profile) claims when both are present.
 */
export function getTokenAccessInfo(
  oidcUser?: IOidcUserLike | null
): ITokenAccessInfo {
  const claims: ITokenClaims = {
    ...(oidcUser?.profile || {}),
    ...(decodeJwtPayload(oidcUser?.access_token) || {}),
  }
  const organization = findClaim(claims, ORGANIZATION_CLAIM_KEYS)
  const { organizationName, customerId } = getOrganizationCustomerId(
    organization
  )
  const tenantCustomerId =
    customerId || asCustomerId(findClaim(claims, CUSTOMER_CLAIM_KEYS))

  return {
    claims,
    customerId: tenantCustomerId,
    organization,
    organizationName,
    hasTenantClaim: tenantCustomerId !== '',
    userInfo: getTokenUserInfo(claims),
  }
}
